// src/pages/AttendanceOverview/attendanceOverviewThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import moment from "moment-timezone";

import {
  getAttendanceCalenderApi,
  getTodayAttendanceOverviewApi,
  getUserAttendancePendingRequestApi,
  markAttendanceApi,
  attendanceRegularizeApi,
  leaveAttendanceApi,
  getHolidayListApi,
  addHolidayApi,
  addRestrictedHolidayApi,
  getAllEmployeeUserApi,
  shiftReportApi,
  attendaceReportApi,
  getTimeSheetApi,
  approveAttendanceReportApi,
  updateStatusAttendancetApi,
  reclassifyAttendanceApi,
  attendaceCheckInStatusApi,
  markRemoteAttendanceApi,
  getLeaveDetailsApi,
  leveAttendanceToEmployeeApi,
  deleteHolidayApi,
  addNewShiftApi,
  deleteShiftManagementApi,
  resendEmailApi,
  getHrLocationsApi,
  editHolidayApi,
  getEmployeeRegularizeTime,
  bulkMarkPresentApi,
  shortLeaveAttendanceApi,
  employeeShiftDatesApi,
  employeeShiftDetailsApi,
  attendanceAnalyticsReportApi,
  getEmployeeAttendanceSummaryApi
} from "../../apiservice/hrpayroll";

import { accessChecker, formatToUtc } from "../../lib/functions";

/**
 * fetchCalendar: month/year optional - returns calendar-related data (holidays, attendance, pending etc)
 */

export const fetchCalendar = createAsyncThunk(
  "attendanceOverview/fetchCalendar",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const body = {
        branch_id: authInfo.userInfo.branch_id,
        user_id: authInfo.userInfo.user_id,
        emp_id: authInfo.employee_id,
        month: payload.month || dayjs().format("MM"),
        year: payload.year || dayjs().format("YYYY"),
        status: "approved",
        institution_id: authInfo.id,
      };
      const res = await getAttendanceCalenderApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * fetchTodaySummary: summary for a specific date or today
 */
export const fetchTodaySummary = createAsyncThunk(
  "attendanceOverview/fetchTodaySummary",
  async (incomingPayload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const tz = authInfo?.time_zone || "Asia/Kolkata";
      const date = incomingPayload?.date || null;
      // const attendance_date = date || dayjs().startOf("day").toISOString();
      // const attendance_date = formatToUtc(date || dayjs().format("YYYY-MM-DD"));
      const selectedDate = date
        ? dayjs(date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      const attendance_date = moment
        .tz(selectedDate, "YYYY-MM-DD", tz)
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
        
      const payload = { 
        attendance_date,
        branch_id: authInfo.userInfo.branch_id,
        institution_id: authInfo.id,
        notIncludeTerminated:incomingPayload?.notIncludeTerminated
      };
      const res = await getTodayAttendanceOverviewApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchEmployeeAttendanceSummary = createAsyncThunk(
  "attendanceOverview/fetchEmployeeAttendanceSummary",
  async (incomingPayload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;

      const tz = authInfo?.time_zone || "Asia/Kolkata";

      const selectedDate = incomingPayload?.date
        ? dayjs(incomingPayload.date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      const attendance_date = moment
        .tz(selectedDate, "YYYY-MM-DD", tz)
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const payload = {
        attendance_date,
        employee_id: authInfo.employee_id,
        branch_id: authInfo.userInfo.branch_id,
        institution_id: authInfo.id,
      };

      const res = await getEmployeeAttendanceSummaryApi(payload);

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


export const fetchLeaveDetails = createAsyncThunk(
  "attendanceOverview/fetchLeaveDetails",
  async (date = null, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const payload = {
        user_id: authInfo.userInfo.user_id,
        pro_id: authInfo.id,
        branch_id: authInfo.userInfo.branch_id,
        institution_id: authInfo.id,
      };
      const res = await getLeaveDetailsApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * fetchPendingRequests - pending user attendance/leave requests
 */
export const fetchPendingRequests = createAsyncThunk(
  "attendanceOverview/fetchPendingRequests",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      // Only pass month/year when the caller (the attendance calendar)
      // explicitly wants a specific month. Everywhere else — the dashboard's
      // Pending Approvals card/modal, Mark Attendance tab, etc. — wants every
      // currently-pending request regardless of when it was submitted;
      // defaulting to the current month here made the count silently drop
      // anything pending from an earlier month.
      const body = {
        branch_id: authInfo.userInfo.branch_id,
        // user_id: authInfo.userInfo.user_id,
        ...(payload.month && payload.year
          ? { month: payload.month, year: payload.year }
          : {}),
        status: "pending",
        institution_id: authInfo.id,
      };
      const res = await getUserAttendancePendingRequestApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * markAttendance - regular mark attendance (uses markAttendanceApi)
 * payload should align with backend: attendance_date, emp_id, in_time/out_time, mode etc.
 */
export const markAttendance = createAsyncThunk(
  "attendanceOverview/markAttendance",
  async (payload = {}, { getState, rejectWithValue }) => {
    console.log("payload : ", payload)
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const body = {
        user_id: authInfo.userInfo.user_id,
        emp_id: authInfo.employee_id != null ? authInfo.employee_id : 0,
        branch_id: authInfo.userInfo.branch_id,
        attendance: payload.action,
        // attendance_date: new Date(),
        attendance_date: formatToUtc(`${payload.date} ${payload.time}`),
        notes: payload.notes,
        status: "inprogress",
        time_zone: authInfo.time_zone,
        institution_id: authInfo.userInfo.pro_id,
      };
      const res = await markAttendanceApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const remoteMarkAttendance = createAsyncThunk(
  "attendanceOverview/remoteMarkAttendance",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const body = {
        user_id: authInfo.userInfo.user_id,
        emp_id: authInfo.employee_id != null ? authInfo.employee_id : 0,
        branch_id: authInfo.userInfo.branch_id,
        attendance: payload.action,
        // attendance_date: new Date(),
        attendance_date: formatToUtc(`${payload.date} ${payload.time}`),
        notes: payload.notes,
        status: "inprogress",
        time_zone: authInfo.time_zone,
        institution_id: authInfo.userInfo.pro_id,
      };
      const res = await markRemoteAttendanceApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * regularizeAttendance - attendanceRegularizeApi
 */
export const regularizeAttendance = createAsyncThunk(
  "attendanceOverview/regularizeAttendance",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let timeZone = authInfo.time_zone || "Asia/Kolkata";

      if (typeof timeZone === "string") {
        try {
          // If it's stringified JSON like "\"Asia/Kolkata\"", parse it
          timeZone = JSON.parse(timeZone);
        } catch {
          // otherwise, keep as-is
        }
      }
      // // 🕒 Convert date-only values to full ISO UTC timestamps in given timezone
      // const dateOnlyConversion = (date) => {
      //   if (!date) return null;
      //   return moment.tz(date, "YYYY-MM-DD", timeZone).toISOString();
      // };

      // // ⏰ Convert time-only values to full ISO UTC timestamps (with dummy date)
      // const timeOnlyConversion = (time) => {
      //   if (!time) return null;
      //   // Combine with today’s date just for ISO compliance
      //   return moment.tz(time, "HH:mm:ss", timeZone).toISOString();
      // };

      let body = {
        user_id: authInfo.userInfo.user_id,
        emp_id: authInfo.employee_id || 0,
        branch_id: authInfo.userInfo.branch_id,
        attendance: payload.attendance || "present",
        from_date: formatToUtc(`${payload.fromDate} 00:00:00`),
        to_date: formatToUtc(`${payload.toDate} 00:00:00`),
        // A non-"present" regularization (absence/leave) has no real
        // check-in/out -- sending them anyway would format "<date>
        // undefined" into a bogus timestamp instead of just omitting it.
        check_in: payload.checkIn ? formatToUtc(`${payload.fromDate} ${payload.checkIn}`) : null,
        check_out: payload.checkOut ? formatToUtc(`${payload.toDate} ${payload.checkOut}`) : null,
        notes: payload.notes,
        status: "pending",
        time_zone: authInfo.time_zone,
        institution_id: authInfo.id,
      };
      const res = await attendanceRegularizeApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * applyLeave - leaveAttendanceApi
 */
export const applyLeave = createAsyncThunk(
  "attendanceOverview/applyLeave",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state    = getState();
      const authInfo = state.auth.auth_info;

      // let timeZone = authInfo.time_zone || "Asia/Kolkata";
      // if (typeof timeZone === "string") {
      //   try { timeZone = JSON.parse(timeZone); } catch {
      //     //  
      //    }
      // }

      // const dateOnlyConversion = (date) => {
      //   if (!date) return null;
      //   return moment.tz(date, "YYYY-MM-DD", timeZone).toISOString();
      // };

      // ── Build FormData ──
      const formData = new FormData();
      formData.append("attendance",     payload.attendance);
      formData.append("user_id",        authInfo.userInfo.user_id);
      formData.append("branch_id",      authInfo.userInfo.branch_id);
      formData.append("emp_id",         authInfo.employee_id || 0);
      formData.append("from_date",      formatToUtc(`${payload.from_date} 00:00:00`));
      formData.append("to_date",        formatToUtc(`${payload.to_date} 00:00:00`));
      formData.append("notes",          payload.notes);
      formData.append("status",         "pending");
      formData.append("institution_id", authInfo.id);

      // ── Attachments only for sick leave ──
      if (payload.attendance === "sick_leave" && payload.attachments?.length) {
        payload.attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      const res = await leaveAttendanceApi(formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// apply short leave 
export const applyShortLeave = createAsyncThunk(
  "attendanceOverview/applyShortLeave",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state    = getState();
      const authInfo = state.auth.auth_info;

      let timeZone = authInfo.time_zone || "Asia/Kolkata";

      if (typeof timeZone === "string") {
        try {
          timeZone = JSON.parse(timeZone);
        } catch {
          // keep as string
        }
      }

      const dateOnlyConversion = (date) => {
        if (!date) return null;

        return moment.tz(date, "YYYY-MM-DD", timeZone).toISOString();
      };

      // ── Build FormData (same as JournalEntry) ──
      const formData = new FormData();
      formData.append("attendance",     payload.attendance);
      formData.append("leave_portion",  payload.leave_portion);
      formData.append("from_date",      dateOnlyConversion(payload.from_date));
      formData.append("to_date",        dateOnlyConversion(payload.to_date));
      formData.append("notes",          payload.notes);
      formData.append("status",         "pending");

      // authInfo fields (same values as before)
      formData.append("user_id",        authInfo.userInfo.user_id);
      formData.append("branch_id",      authInfo.userInfo.branch_id);
      formData.append("emp_id",         authInfo.employee_id || 0);
      formData.append("institution_id", authInfo.id);

      // ── Attachments (raw File objects from ShortLeaveModal) ──
      payload.attachments?.forEach((file) => {
        formData.append("attachments", file);
      });

      const res = await shortLeaveAttendanceApi(formData);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// add leave to employee

export const addLeaveToEmp = createAsyncThunk(
  "attendanceOverview/addLeaveToEmp",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let timeZone = authInfo.time_zone || "Asia/Kolkata";

      if (typeof timeZone === "string") {
        try {
          timeZone = JSON.parse(timeZone);
        } catch {
          //string
        }
      }

      const dateOnlyConversion = (date) => {
        if (!date) return null;

        return moment
          .tz(date, "YYYY-MM-DD", timeZone)
          .startOf("day")
          .utc()
          .format();
      };    

      const body = {
        attendance: payload.attendance,
        user_id: authInfo.userInfo.user_id,
        from_date: dateOnlyConversion(payload.from_date),
        to_date: dateOnlyConversion(payload.to_date),
        branch_id: authInfo.userInfo.branch_id,
        notes: payload.notes,
        institution_id: authInfo.userInfo.pro_id,
        emp_id: payload.emp_id,
        status: "approved",
      };
      const res = await leveAttendanceToEmployeeApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * fetchHolidayList & addHoliday
 */
export const fetchHolidayList = createAsyncThunk(
  "attendanceOverview/fetchHolidayList",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const body = {
        holiday_type: payload.holiday_type,
        startDate: payload.fromDate,
        endDate: payload.toDate,
      };
      const res = await getHolidayListApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addHoliday = createAsyncThunk(
  "attendanceOverview/addHoliday",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let body = {
        branch_id: authInfo.userInfo.branch_id,
        holiday_type: payload.holiday_type,
        holiday_name: payload.holiday_name,
        role_id: "",
        location: "",
        notes: payload.notes,
        holiday_date: payload.holiday_date,
        institution_id: authInfo.id,
      };
      const res = await addHolidayApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const editHoliday = createAsyncThunk(
  "attendanceOverview/editHoliday",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let body = {
        branch_id: authInfo.userInfo.branch_id,
        holiday_type: payload.holiday_type,
        holiday_name: payload.holiday_name,
        role_id: "",
        location: "",
        notes: payload.notes,
        holiday_date: payload.holiday_date,
        institution_id: authInfo.id,
        holiday_id: payload.id,
      };
      const res = await editHolidayApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addRestrictedHoliday = createAsyncThunk(
  "attendanceOverview/addRestrictedHoliday",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let body = {
        branch_id: authInfo.userInfo.branch_id,
        institution_id: authInfo.id,
        notes: payload.notes,
        restricted_holiday_date: payload.restricted_holiday_date,
      };
      const res = await addRestrictedHolidayApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * fetch employees for dropdowns
 */
export const fetchAllEmployees = createAsyncThunk(
  "attendanceOverview/fetchAllEmployees",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const res = await getAllEmployeeUserApi({ institution_id: authInfo.id, notIncludeTerminated:payload.notIncludeTerminated });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchEmployeeShiftDates = createAsyncThunk(
  "attendanceOverview/fetchEmployeeShiftDates",
  async (date, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const auth = state.auth.auth_info;

      const body = {
        emp_id: auth.employee_id,
        institution_id: auth.id,
        month: dayjs(date).format("MM"),
        year: dayjs(date).format("YYYY"),
      };

      const res = await employeeShiftDatesApi(body);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * shift report
 */
export const fetchShiftReport = createAsyncThunk(
  "attendanceOverview/fetchShiftReport",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const body = {
        institution_id: authInfo.id,
        startDate: payload.fromDate,
        endDate: payload.toDate,
        employee_id: accessChecker("approve_attendance")
          ? null
          : authInfo.employee_id,
      };
      const res = await shiftReportApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * attendance report (details)
 */
export const fetchAttendanceReport = createAsyncThunk(
  "attendanceOverview/fetchAttendanceReport",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const body = {
        institution_id: authInfo.id,
        fromDate: payload.fromDate,
        toDate: payload.toDate,
        employee_id: payload.employee_id,
      };
      const res = await attendaceReportApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * fetchTimeSheet
 */
export const fetchTimeSheet = createAsyncThunk(
  "attendanceOverview/fetchTimeSheet",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const res = await getTimeSheetApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * approve attendance (bulk or single)
 */
export const fetchApproveAttendanceReport = createAsyncThunk(
  "attendanceOverview/approveAttendanceReport",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const timeZone = authInfo.time_zone || "UTC";
      const normalizeDate = (date) => {
        if (!date) return null;
        return dayjs(date).tz(timeZone).format("YYYY-MM-DD");
      };

      const body = {
        branch_id: authInfo.userInfo.branch_id,
        employee_id: payload.empId,
        status: payload.status,
        start_date: normalizeDate(payload.fromDate),
        end_date: normalizeDate(payload.toDate),
        institution_id: authInfo.id,
      };
      const res = await approveAttendanceReportApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * update status (used in approve tab)
 */
export const updateAttendanceStatus = createAsyncThunk(
  "attendanceOverview/updateAttendanceStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await updateStatusAttendancetApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const reclassifyAttendance = createAsyncThunk(
  "attendanceOverview/reclassifyAttendance",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await reclassifyAttendanceApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
export const fetchcheckInStatus = createAsyncThunk(
  "attendanceOverview/fetchcheckInStatus",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const payload = {
        emp_id: authInfo.employee_id,
      };
      const res = await attendaceCheckInStatusApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteHoliday = createAsyncThunk(
  "attendanceOverview/deleteHoliday",
  async ({ holiday_id }, { rejectWithValue }) => {
    try {
      const response = await deleteHolidayApi({ holiday_id: holiday_id });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addNewShift = createAsyncThunk(
  "attendanceOverview/addNewShift",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let timeZone = authInfo.time_zone || "Asia/Kolkata";

      if (typeof timeZone === "string") {
        try {
          // If it's stringified JSON like "\"Asia/Kolkata\"", parse it
          timeZone = JSON.parse(timeZone);
        } catch {
          /* ignore invalid JSON */
        }
      }
      // 🕒 Convert date-only values to full ISO UTC timestamps in given timezone
      const dateOnlyConversion = (date) => {
        if (!date) return null;
        return moment.tz(date, "YYYY-MM-DD", timeZone).toISOString();
      };

      const timeOnlyConversion = (time) => {
        if (!time) return null;
        // Combine with today’s date just for ISO compliance
        return moment.tz(time, "HH:mm:ss", timeZone).toISOString();
      };
      let body = {
        employee_id: payload.employee_id,
        branch_id: authInfo.userInfo.branch_id,
        startdate: dateOnlyConversion(payload.startDate),
        enddate: dateOnlyConversion(payload.endDate),
        starttime: payload.check_in,
        endtime: payload.check_out,
        location: payload.location,
        notes: payload.notes,
        institution_id: authInfo.id,
        user_roles: null,
      };
      const res = await addNewShiftApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteShift = createAsyncThunk(
  "attendanceOverview/deleteShift",
  async (payLoad, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      let timeZone = authInfo.time_zone || "Asia/Kolkata";

      if (typeof timeZone === "string") {
        try {
          // If it's stringified JSON like "\"Asia/Kolkata\"", parse it
          timeZone = JSON.parse(timeZone);
        } catch {
          /* ignore invalid JSON */
        }
      }
      // 🕒 Convert date-only values to full ISO UTC timestamps in given timezone
      const dateOnlyConversion = (date) => {
        if (!date) return null;
        return moment.tz(date, "YYYY-MM-DD", timeZone).toISOString();
      };

      const body = {
        employee_id: payLoad.employee_id,
        enddate: dateOnlyConversion(payLoad.enddate),
        startdate: dateOnlyConversion(payLoad.startdate),
      };

      const response = await deleteShiftManagementApi(body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEmployeeShiftDetails = createAsyncThunk(
  "attendanceOverview/fetchEmployeeShiftDetails",

  async (payload, { rejectWithValue }) => {
    try {
      const res = await employeeShiftDetailsApi(payload);

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const resendEmail = createAsyncThunk(
  "attendanceOverview/resendEmail",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await resendEmailApi(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchHrLocation = createAsyncThunk(
  "attendanceOverview/fetchHrLocation",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const payload = {
        branch_id: authInfo.userInfo.branch_id,
      };
      const response = await getHrLocationsApi(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEmployeeRegularizeTime = createAsyncThunk(
  "attendanceOverview/fetchEmployeeRegularizeTime",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authInfo = state.auth.auth_info;
      const body = {
        emp_id: authInfo.employee_id,
        date: payload.date
      };
      const res = await getEmployeeRegularizeTime(body);
   
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


export const bulkMarkPresent = createAsyncThunk(
  "attendanceOverview/bulkMarkPresent",
  async (payload, {getState, rejectWithValue }) => {
    try {
    const state = getState();
    const authInfo = state.auth.auth_info;
    let timeZone = authInfo.time_zone || "Asia/Kolkata";

    if (typeof timeZone === "string") {
      try {
        timeZone = JSON.parse(timeZone);
      } catch { /* not JSON — keep the raw string value */ }
    }

    const dateOnlyConversion = (date) => {
      if (!date) return null;

      return moment
        .tz(date, "YYYY-MM-DD", timeZone)
        .startOf("day")
        .utc()
        .format();
    };

      const body = {
        ...payload,
        from_date: dateOnlyConversion(payload.from_date),
        to_date: dateOnlyConversion(payload.to_date),
      };

      const res = await bulkMarkPresentApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * attendance analytics report 
 */
export const fetchAttendanceAnalyticsReport = createAsyncThunk(
  "attendanceOverview/fetchAttendanceAnalyticsReport",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const body = {
        month: payload.month,
        year: payload.year,
        employee_id: payload.employee_id || null,
      };

      const res = await attendanceAnalyticsReportApi(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);